import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import accountService from '../services/accountService';
import './PartnerForm.css';

const ACCOUNTS_PER_PAGE = 10;

const PartnerHotelChains = () => {
  const navigate = useNavigate();
  const { selectedPartner, accountsRefreshKey } = useAccount();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const partnerId = selectedPartner;

  useEffect(() => {
    if (!partnerId) return;
    setLoading(true);
    setError(null);
    accountService.getAllAccounts({ partnerid: partnerId })
      .then(res => {
        const data = res.data?.accounts || res.accounts || (Array.isArray(res.data) ? res.data : []);
        setAccounts(data);
      })
      .catch(() => setError('Failed to load hotel chains'))
      .finally(() => setLoading(false));
  }, [partnerId, accountsRefreshKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, partnerId]);

  const handleDelete = async (accountId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete hotel chain "${companyName}"?`)) return;
    try {
      await accountService.deleteAccount(accountId);
      setAccounts(prev => prev.filter(a => {
        const id = a.ACCOUNTID || a.accountid || a.id;
        return id !== accountId;
      }));
    } catch (err) {
      alert('Failed to delete hotel chain: ' + (err.response?.data?.message || err.message));
    }
  };

  if (!partnerId) {
    return (
      <div className="partner-form-container">
        <div className="page-header">
          <h1>Hotel Chains</h1>
        </div>
        <div className="no-data">
          <p>Select a partner from the top bar to view hotel chains.</p>
        </div>
      </div>
    );
  }

  const filtered = accounts.filter(acc => {
    const name = acc.COMPANYNAME || acc.companyname || acc.name || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const totalPages = Math.ceil(filtered.length / ACCOUNTS_PER_PAGE);
  const page = Math.min(currentPage, totalPages || 1);
  const pageItems = filtered.slice((page - 1) * ACCOUNTS_PER_PAGE, page * ACCOUNTS_PER_PAGE);

  return (
    <div className="partner-form-container">
      <div className="page-header">
        <h1>Hotel Chains</h1>
        <p className="page-subtitle">Hotel chains associated with this partner</p>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <div className="tab-panel">
        <div className="tab-panel-header">
          <input
            type="text"
            className="search-input"
            placeholder="Search by hotel chain name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Link to={`/partners/${partnerId}/account/new`} state={{ returnTo: '/partner-hotel-chains' }} className="btn-add">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Hotel Chain
          </Link>
        </div>

        {loading ? (
          <div className="loading">Loading hotel chains...</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company Name</th>
                  <th>Country</th>
                  <th>Currency</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <path d="M32 22c3.32 0 6-2.68 6-6s-2.68-6-6-6-6 2.68-6 6 2.68 6 6 6zM16 22c3.32 0 6-2.68 6-6s-2.68-6-6-6-6 2.68-6 6 2.68 6 6 6zm0 4c-4.66 0-14 2.34-14 7v3h28v-3c0-4.66-9.34-7-14-7z" fill="#d1d5db"/>
                      </svg>
                      <p>{search ? `No hotel chains match "${search}"` : 'No hotel chains found'}</p>
                    </td>
                  </tr>
                ) : (
                  pageItems.map(acc => {
                    const accountId = acc.ACCOUNTID || acc.accountid || acc.id;
                    const companyName = acc.COMPANYNAME || acc.companyname || acc.name;
                    const country = acc.COUNTRY || acc.country || '-';
                    const currency = acc.CURRENCY || acc.currency || '-';
                    const status = acc.ALLOWYN || acc.allowyn || acc.allowYn || 'N';

                    return (
                      <tr
                        key={accountId}
                        className="clickable-row"
                        onClick={() => navigate(`/accounts/${accountId}`)}
                        title="Click to view"
                      >
                        <td><span className="id-badge">{accountId}</span></td>
                        <td className="primary-text">{companyName}</td>
                        <td>{country}</td>
                        <td><span className="currency-badge">{currency}</span></td>
                        <td>
                          <span className={`status-badge status-${status === 'Y' ? 'active' : 'inactive'}`}>
                            {status === 'Y' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            className="btn-action btn-edit-action"
                            onClick={e => { e.stopPropagation(); navigate(`/accounts/${accountId}/edit`); }}
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                          <button
                            className="btn-action btn-delete-action"
                            onClick={e => { e.stopPropagation(); handleDelete(accountId, companyName); }}
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={page === 1}>
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={page >= totalPages}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerHotelChains;
